import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Copy, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TextField, SelectField } from "@/components/poshan/FormBits";
import RelationPicker from "@/components/poshan/RelationPicker";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { useActiveSchool } from "@/lib/activeSchool";

/**
 * Generic master data manager. Renders a list, create/edit dialog, delete confirm.
 *
 * props:
 *   title                — section heading
 *   testIdPrefix         — kebab-case prefix for data-testids
 *   columns              — [{ key, label }]   — table columns to display
 *   formFields           — [{ key, label, type?, options?, required? }]
 *   emptyRecord          — initial record for new entry
 *   api                  — { list, upsert, remove }
 *   copyFromGroupKey     — optional: when present, the Add dialog shows a "Copy from existing"
 *                          dropdown grouped by this field (e.g., 'category'). Selecting an item
 *                          pre-fills all form fields except `name` (so a unique name must be set).
 */
export default function MasterCrud({
  title, testIdPrefix, columns, formFields, emptyRecord, api, copyFromGroupKey,
  // Optional: intercept the "Add" click. Receives `openForm(seedDraft?)`
  // which the caller invokes to actually open the dialog (optionally with
  // a pre-filled draft). Returning a Promise that resolves without calling
  // openForm() cancels the open silently.
  addInterceptor,
  // Optional: override the per-row save call. Receives `(draft, isEditing)`
  // and must return a Promise<savedRecord | savedRecord[]>. Used by Setup
  // to broadcast one vendor record across all categories.
  saveOverride,
  // Optional fields-filter: hide certain keys from the form when set
  // (e.g. drop "category" when broadcasting across all categories).
  hideFieldKeys,
}) {
  const { user } = useAuth();
  const { profiles } = useActiveSchool();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // record being edited or null for new
  const [draft, setDraft] = useState(emptyRecord);
  const [copySource, setCopySource] = useState(""); // human-readable source name (banner)

  // School lookup for chip labels.
  const schoolLookup = useMemo(() => {
    const map = new Map();
    (profiles || []).forEach((p) => { map.set(p.id, p.schoolName || p.udiseCode || p.id); });
    return map;
  }, [profiles]);

  const renderSchoolChip = (sid, onRemove, location = "row") => {
    const label = schoolLookup.get(sid) || sid.slice(0, 8);
    return (
      <span
        key={sid}
        data-testid={`${testIdPrefix}-${location}-school-chip-${sid}`}
        className="inline-flex items-center gap-1 rounded-full border border-clay/40 bg-clay/10 px-2.5 py-1 text-xs font-medium text-clay-dark"
      >
        <span className="max-w-[180px] truncate">{label}</span>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(sid)}
            aria-label={`Remove ${label}`}
            data-testid={`${testIdPrefix}-${location}-school-chip-remove-${sid}`}
            className="rounded-full p-0.5 transition-colors hover:bg-clay/20"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  };

  // Identify the "name" field (first required field, fallback to first field with key=='name')
  const nameField = useMemo(
    () => formFields.find((f) => f.required) || formFields.find((f) => f.key === "name") || formFields[0],
    [formFields],
  );
  const nameFieldKey = nameField?.key || "name";
  const nameFieldLabel = nameField?.label || "Name";

  // Build grouped list for the copy-from dropdown
  const copyFromOptions = useMemo(() => {
    if (!copyFromGroupKey) return [];
    const groups = new Map();
    items.forEach((it) => {
      const grpVal = String(it[copyFromGroupKey] ?? "").trim() || "Uncategorised";
      if (!groups.has(grpVal)) groups.set(grpVal, []);
      groups.get(grpVal).push(it);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, list]) => ({ label, items: list.sort((a, b) => (a[nameFieldKey] || "").localeCompare(b[nameFieldKey] || "")) }));
  }, [items, copyFromGroupKey, nameFieldKey]);

  const handleCopyFrom = (id) => {
    if (!id) return;
    const src = items.find((it) => it.id === id);
    if (!src) return;
    // Don't allow copying a vendor onto itself when editing.
    if (editing && src.id === editing.id) {
      toast.error("Cannot copy a record onto itself — pick a different one");
      return;
    }
    // Clone every form-field value except the unique `name` field.
    // - In Add mode: leave name empty so the user is forced to enter a fresh, unique name.
    // - In Edit mode: keep the current draft name (the record being edited keeps its identity).
    const cloned = { ...emptyRecord };
    formFields.forEach((f) => {
      if (f.key === nameFieldKey) return;
      if (src[f.key] !== undefined && src[f.key] !== null) cloned[f.key] = src[f.key];
    });
    cloned[nameFieldKey] = editing ? (draft[nameFieldKey] || "") : "";
    setDraft(cloned);
    setCopySource(src[nameFieldKey] || "(unnamed)");
    toast.success(
      editing
        ? `Cloned details from ${src[nameFieldKey] || "record"} — ${nameFieldLabel} preserved`
        : `Cloned from ${src[nameFieldKey] || "record"} — set a new ${nameFieldLabel}`,
    );
  };

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await api.list();
      setItems(data);
    } catch (err) {
      toast.error(`Could not load ${title.toLowerCase()}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = (seedDraft) => {
    setEditing(null);
    setDraft({ ...emptyRecord, ...(seedDraft || {}) });
    setCopySource("");
    setDialogOpen(true);
  };

  // The "Add" button calls this. When addInterceptor is set we hand it the
  // openNew function so it can show its own pre-flight UI and decide whether
  // / how to open the form.
  const handleAddClick = () => {
    if (typeof addInterceptor === "function") {
      try {
        addInterceptor(openNew);
      } catch (e) {
        // Fall back to opening the form normally if the interceptor blows up.
        console.error("addInterceptor threw", e);
        openNew();
      }
      return;
    }
    openNew();
  };

  const openEdit = (record) => {
    setEditing(record);
    setDraft({ ...emptyRecord, ...record });
    setCopySource("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // iter124 — fields may declare `showIf(draft)` so they are hidden/skipped
    // when not applicable (e.g. father-name fields are skipped for agency-mode
    // vendors/CCHs). Don't validate fields the user can't see.
    const visibleFields = formFields.filter((f) => (typeof f.showIf === "function" ? f.showIf(draft) : true));
    const required = visibleFields.filter((f) => f.required);
    const missing = required.find((f) => !String(draft[f.key] ?? "").trim());
    if (missing) {
      // Prefer the Odia-specific message when the form provides one,
      // otherwise fall back to a generic English message.
      toast.error(missing.requiredMsg || `${missing.label} ବାଧ୍ୟତାମୂଳକ — ଅନୁଗ୍ରହ କରି ପୂରଣ କରନ୍ତୁ`);
      return;
    }
    // Admin gate: a vendor / CCH must be tagged to at least one school.
    if (isAdmin) {
      const tags = Array.isArray(draft.schoolIds) ? draft.schoolIds : [];
      if (tags.length === 0) {
        toast.error("Tag at least one school — the record won't show up on any school dashboard otherwise");
        return;
      }
    }
    try {
      const saved = typeof saveOverride === "function"
        ? await saveOverride(draft, !!editing)
        : await api.upsert(draft, editing?.id);
      // saveOverride may return an array (bulk create). Normalize to first
      // record for the toast-label calculation.
      const primary = Array.isArray(saved) ? saved[0] : saved;
      const bulkCount = Array.isArray(saved) ? saved.length : 0;
      const taggedNames = (primary?.schoolIds || draft.schoolIds || []).map((id) => schoolLookup.get(id) || id.slice(0, 8));
      toast.success(
        bulkCount > 1
          ? `${bulkCount} ${title.toLowerCase()} records saved`
          : isAdmin && taggedNames.length
            ? `${editing ? "Updated" : "Added"} — tagged to ${taggedNames.length} school${taggedNames.length === 1 ? "" : "s"}: ${taggedNames.join(", ")}`
            : (editing ? "Updated" : "Added"),
      );
      setDialogOpen(false);
      await refresh();
    } catch (err) {
      toast.error("Save failed");
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.remove(id);
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error("Delete failed");
      console.error(err);
    }
  };

  return (
    <section
      data-testid={`${testIdPrefix}-section`}
      className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8"
    >
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-clay">Master Data</p>
          <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-ink-900 sm:text-3xl">{title}</h2>
        </div>
        <Button data-testid={`${testIdPrefix}-add`} onClick={handleAddClick} className="gap-2 bg-clay text-white hover:bg-clay-dark">
          <Plus className="h-4 w-4" /> Add new
        </Button>
      </div>

      {/* Mobile vertical card list (<sm). Each card is the same record as
          the desktop row but stacked top-to-bottom for easier reading on
          phones. The wider table below is `hidden sm:block`. */}
      <div className="space-y-3 sm:hidden" data-testid={`${testIdPrefix}-mobile-list`}>
        {loading && (
          <p className="rounded-xl bg-sand-50 px-3 py-2 text-center text-xs text-ink-700">Loading…</p>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-border bg-white/50 p-6 text-center text-sm text-ink-700">
            No records yet. Tap <b>Add new</b> to create one.
          </div>
        )}
        {items.map((row) => {
          // Pick the "name" column as the card title — first column or `name` key.
          const nameKey = (columns.find((c) => c.key === "name") || columns[0])?.key || "name";
          const restCols = columns.filter((c) => c.key !== nameKey);
          return (
            <article
              key={row.id}
              data-testid={`${testIdPrefix}-card-${row.id}`}
              className="rounded-2xl border border-border bg-white p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)]"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 flex-1 truncate font-heading text-base font-bold text-ink-900">
                  {row[nameKey] || <span className="text-ink-700">—</span>}
                </h3>
                <div className="shrink-0 flex gap-1">
                  <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-edit-mobile-${row.id}`} onClick={() => openEdit(row)} className="h-9 w-9 rounded-full p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-9 w-9 rounded-full p-0 text-destructive hover:bg-red-50 hover:text-destructive" data-testid={`${testIdPrefix}-delete-mobile-${row.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                        <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(row.id)} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                {restCols.map((col) => (
                  <div key={col.key} className={col.type === "number" ? "" : "col-span-2 sm:col-span-1"}>
                    <dt className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{col.label}</dt>
                    <dd className="mt-0.5 break-words text-ink-900">{row[col.key] || <span className="text-ink-700">—</span>}</dd>
                  </div>
                ))}
              </dl>
            </article>
          );
        })}
      </div>

      {/* Desktop / tablet table (≥sm) — unchanged. */}
      <div className="hidden overflow-x-auto rounded-xl border border-border sm:block">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead className="bg-sand-100 text-left text-ink-700">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">{col.label}</th>
              ))}
              {isAdmin && (
                <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Tagged Schools</th>
              )}
              <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + (isAdmin ? 2 : 1)} className="p-6 text-center text-ink-700">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={columns.length + (isAdmin ? 2 : 1)} className="p-8 text-center text-ink-700">
                No records yet. Click <b>Add new</b> to create one.
              </td></tr>
            )}
            {items.map((row) => (
              <tr key={row.id} data-testid={`${testIdPrefix}-row-${row.id}`} className="border-t border-border">
                {columns.map((col) => (
                  <td key={col.key} className="p-3 text-ink-900">{row[col.key] || <span className="text-ink-700">—</span>}</td>
                ))}
                {isAdmin && (
                  <td className="p-3" data-testid={`${testIdPrefix}-tagged-cell-${row.id}`}>
                    <div className="flex flex-wrap gap-1">
                      {((row.schoolIds || (row.schoolId ? [row.schoolId] : []))).length === 0 ? (
                        <span className="text-xs italic text-ochre">Untagged</span>
                      ) : (
                        (row.schoolIds || [row.schoolId]).map((sid) => renderSchoolChip(sid))
                      )}
                    </div>
                  </td>
                )}
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-edit-${row.id}`} onClick={() => openEdit(row)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`${testIdPrefix}-delete-${row.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(row.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <span style={{ display: "none" }} />
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title.replace(/s$/, "")}` : `Add new ${title.replace(/s$/, "")}`}</DialogTitle>
            <DialogDescription>Fields marked * are required.</DialogDescription>
          </DialogHeader>
          {copyFromGroupKey && copyFromOptions.length > 0 && (
            <div className="mb-1 rounded-lg border border-clay/30 bg-clay/5 p-3" data-testid={`${testIdPrefix}-copy-from-row`}>
              <p className="mb-2 flex items-center gap-1.5 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-clay">
                <Copy className="h-3 w-3" /> {editing ? "Copy details from another record" : "Copy from existing — fast add"}
              </p>
              <p className="mb-2 text-xs text-ink-700">
                {editing
                  ? <>Pick another {title.toLowerCase().replace(/s$/, "")} to copy all fields except <b>{nameFieldLabel}</b> (kept as <b>{draft[nameFieldKey] || "—"}</b>). Useful for quickly aligning shared address / {copyFromGroupKey} info.</>
                  : <>Pick an existing record to clone all fields (except <b>{nameFieldLabel}</b>). Useful when adding multiple {title.toLowerCase()} that share the same {copyFromGroupKey}/village/post.</>}
              </p>
              <select
                data-testid={`${testIdPrefix}-copy-from-select`}
                value=""
                onChange={(e) => handleCopyFrom(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-white px-3 text-sm focus:border-clay focus:outline-none"
              >
                <option value="">— Select a record to clone —</option>
                {copyFromOptions.map((grp) => (
                  <optgroup key={grp.label} label={grp.label}>
                    {grp.items
                      .filter((it) => !editing || it.id !== editing.id)
                      .map((it) => (
                        <option key={it.id} value={it.id}>
                          {(it[nameFieldKey] || "(unnamed)")}
                          {it.village ? ` — ${it.village}` : ""}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
              {copySource && (
                <p className="mt-1.5 text-xs text-moss-dark" data-testid={`${testIdPrefix}-copy-from-applied`}>
                  ✓ Cloned from <b>{copySource}</b> — {editing ? <>review &amp; adjust fields below, then <b>Update</b></> : <>review &amp; edit fields below, set the new <b>{nameFieldLabel}</b>, then <b>Add</b></>}.
                </p>
              )}
            </div>
          )}
          <div className="grid gap-4 py-4 sm:grid-cols-2">
            {isAdmin && (
              <div className="sm:col-span-2 rounded-lg border border-moss/30 bg-moss/5 p-3" data-testid={`${testIdPrefix}-school-tag-block`}>
                <p className="mb-2 flex items-center gap-1.5 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-moss-dark">
                  🏫 Tag Schools — record will appear on these schools' setup pages
                </p>
                <p className="mb-2 text-xs text-ink-700">
                  Pick one or more schools to attach this {title.toLowerCase().replace(/s$/, "")} to. Each school user will see it under their own Setup → {title}.
                </p>
                <div className="mb-2 flex flex-wrap gap-1.5 min-h-[28px]" data-testid={`${testIdPrefix}-tagged-schools-row`}>
                  {(draft.schoolIds || []).length === 0 ? (
                    <span className="text-xs italic text-ochre">No schools tagged yet — pick one below.</span>
                  ) : (
                    (draft.schoolIds || []).map((sid) =>
                      renderSchoolChip(sid, (rid) => setDraft((d) => ({ ...d, schoolIds: (d.schoolIds || []).filter((x) => x !== rid) })), "dialog"),
                    )
                  )}
                </div>
                <select
                  data-testid={`${testIdPrefix}-school-tag-select`}
                  value=""
                  onChange={(e) => {
                    const sid = e.target.value;
                    if (!sid) return;
                    setDraft((d) => {
                      const cur = d.schoolIds || [];
                      if (cur.includes(sid)) return d;
                      return { ...d, schoolIds: [...cur, sid] };
                    });
                  }}
                  className="h-10 w-full rounded-md border border-border bg-white px-3 text-sm focus:border-moss focus:outline-none"
                >
                  <option value="">— Add a school —</option>
                  {(profiles || [])
                    .filter((p) => !(draft.schoolIds || []).includes(p.id))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.schoolName || "(unnamed)"}{p.udiseCode ? ` · ${p.udiseCode}` : ""}
                      </option>
                    ))}
                </select>
              </div>
            )}
            {formFields
              .filter((f) => !(hideFieldKeys || []).includes(f.key))
              .filter((f) => (typeof f.showIf === "function" ? f.showIf(draft) : true))
              .map((f) => {
                // iter153 — field.labelFn(draft) lets a field recompute its
                // label from live form state (e.g. "Father's / Husband's"
                // → "Father's Name (English)" once the user picks Father).
                const effectiveLabel = typeof f.labelFn === "function" ? f.labelFn(draft) : f.label;
                if (f.type === "select") {
                  return (
                    <SelectField
                      key={f.key}
                      label={effectiveLabel}
                      required={f.required}
                      value={draft[f.key] ?? ""}
                      onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                      options={f.options}
                      testId={`${testIdPrefix}-input-${f.key}`}
                    />
                  );
                }
                if (f.type === "relation") {
                  // iter153 — Father / Husband radio chips. Fills the
                  // entire row (sm:col-span-2) so the two 48-px chips sit
                  // side by side at tablet / desktop widths.
                  return (
                    <div key={f.key} className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-semibold text-ink-700">
                        {effectiveLabel}{f.required && <span className="ml-0.5 text-rose-600">*</span>}
                      </label>
                      <RelationPicker
                        value={draft[f.key] ?? "father"}
                        onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                        testIdPrefix={`${testIdPrefix}-form`}
                      />
                    </div>
                  );
                }
                return (
                  <TextField
                    key={f.key}
                    label={effectiveLabel}
                    type={f.type || "text"}
                    required={f.required}
                    value={draft[f.key] ?? ""}
                    onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                    testId={`${testIdPrefix}-input-${f.key}`}
                  />
                );
              },
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid={`${testIdPrefix}-save`} className="bg-clay text-white hover:bg-clay-dark">
              {editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
